import { useParticularPoolsSelector } from "src/context/poolsContext";
import { useNetworkProviderSelector } from "src/ether-utils/context/providerContext";
import TToken from "src/assets/abis/tokens/TToken.json";
import DebtToken from "src/assets/abis/tokens/DebtToken.json";
import { useEffect, useMemo, useState } from "react";
import { Multicall } from "ethereum-multicall";
import { convertBigNumber } from "src/ether-utils/utils";

export default function useTravaUserAction(accountAddress, pool, network) {
  const { token, fetch, fetchTravaPrice } = useParticularPoolsSelector(pool);
  const provider = useNetworkProviderSelector(network);
  const [data, setData] = useState(null);

  const multiCall = useMemo(() => {
    if (provider) return new Multicall({ ethersProvider: provider, tryAggregate: true });
    return null;
  }, [provider]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(async () => {
    if (token && fetchTravaPrice && multiCall) {
      const _tokenAddresses = Object.keys(token);
      let _dataCall = [];
      for (let address of _tokenAddresses) {
        const _data = token[address];
        _dataCall.push({
          reference: `t_${address}`,
          contractAddress: _data.tTokenAddress,
          abi: TToken,
          calls: [{ reference: "balanceOf", methodName: "balanceOf", methodParameters: [accountAddress] }],
        });
        _dataCall.push({
          reference: `d_${address}`,
          contractAddress: _data.debtTokenAddress,
          abi: DebtToken,
          calls: [{ reference: "balanceOf", methodName: "balanceOf", methodParameters: [accountAddress] }],
        });
      }
      const result = (await multiCall.call(_dataCall)).results;
      const _price = await fetchTravaPrice();
      let actionData = [];
      let totalUserDeposit = 0;
      let totalUserBorrow = 0;
      let netApy = 0;
      for (let address of _tokenAddresses) {
        const _data = token[address];
        const _tokenPrice = _price[address].price;
        const _tRawData = result[`t_${address}`].callsReturnContext[0].returnValues[0];
        const _dRawData = result[`d_${address}`].callsReturnContext[0].returnValues[0];
        const _tokenDeposit = convertBigNumber(_tRawData, _data.decimal, 10) * _tokenPrice;
        const _tokenBorrow = convertBigNumber(_dRawData, _data.decimal, 10) * _tokenPrice;

        totalUserDeposit += _tokenDeposit;
        totalUserBorrow += _tokenBorrow;
        netApy += _tokenDeposit * _data.depositRate - _tokenBorrow * _data.borrowRate;
        if (_tokenDeposit > 0 || _tokenBorrow > 0)
          actionData.push({
            ..._data,
            deposit: _tokenDeposit,
            borrow: _tokenBorrow,
          });
      }
      setData({ ...{ totalUserDeposit, totalUserBorrow, actionData }, netApy: netApy / totalUserDeposit });
    }
  }, [token, fetchTravaPrice, multiCall, accountAddress]);

  return useMemo(() => {
    if (data) return data;
    return { totalDeposit: 0, totalBorrow: 0, actionData: [], netApy: Infinity };
  }, [data]);
}
